import React from 'react';
import { BookOpen, Building2, CalendarDays, Clock3, Download, MapPin, RefreshCw } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import ActionToast from '../../../components/ui/ActionToast';
import { academicCalendarService } from '../../../services/academicCalendarService';
import { systemService } from '../../../services/systemService';
import { timetableService } from '../../../services/timetableService';

const DAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOUR_MINUTES = 60;
const HOUR_ROW_HEIGHT = 64;

const minutesFromTime = value => {
  const [hours, minutes] = String(value || '00:00').slice(0, 5).split(':').map(Number);
  return (hours * 60) + minutes;
};

const formatTime = minutes => {
  const normalized = Math.max(0, Number(minutes) || 0);
  return `${String(Math.floor(normalized / 60)).padStart(2, '0')}:${String(normalized % 60).padStart(2, '0')}`;
};

const MyTimetable = ({ audience = 'trainer', onBack }) => {
  const { user, profile } = useAuth();
  const isStudent = audience === 'student';
  const [years, setYears] = React.useState([]);
  const [semesters, setSemesters] = React.useState([]);
  const [yearId, setYearId] = React.useState('');
  const [semesterId, setSemesterId] = React.useState('');
  const [sessions, setSessions] = React.useState([]);
  const [scheduleWindow, setScheduleWindow] = React.useState({ startsAt: '07:00', endsAt: '20:00' });
  const [loading, setLoading] = React.useState(true);
  const [notice, setNotice] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    Promise.all([academicCalendarService.getAcademicYears(), systemService.getInstitutionDetails()]).then(([rows, settings]) => {
      if (!active) return;
      const result = rows || [];
      const current = result.find(row => row.status === 'active') || result[0];
      setYears(result);
      setYearId(current?.id || '');
      setScheduleWindow({
        startsAt: String(settings?.timetable_day_starts_at || '07:00').slice(0, 5),
        endsAt: String(settings?.timetable_day_ends_at || '20:00').slice(0, 5),
      });
    }).catch(error => setNotice({ type: 'error', message: error.message })).finally(() => setLoading(false));
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (!yearId) {
      setSemesters([]);
      setSemesterId('');
      return;
    }
    academicCalendarService.getSemesters(yearId).then(rows => {
      const result = rows || [];
      const current = result.find(row => row.status === 'active') || result[0];
      setSemesters(result);
      setSemesterId(current?.id || '');
    }).catch(error => setNotice({ type: 'error', message: error.message }));
  }, [yearId]);

  const load = React.useCallback(async () => {
    if (!yearId || !semesterId || !user?.id) {
      setSessions([]);
      return;
    }
    setLoading(true);
    try {
      const rows = isStudent
        ? await timetableService.getStudentSessions(yearId, semesterId)
        : await timetableService.getSessions(yearId, semesterId);
      setSessions(isStudent ? (rows || []) : (rows || []).filter(row => String(row.session?.trainer_id || row.trainer?.id) === String(user.id)));
    } catch (error) {
      setNotice({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  }, [isStudent, semesterId, user?.id, yearId]);

  React.useEffect(() => { load(); }, [load]);

  const displaySessions = React.useMemo(() => {
    const grouped = new Map();
    sessions.forEach(row => {
      const groupCode = row.session?.teaching_group_code;
      const key = groupCode
        ? [row.session?.day_of_week, row.session?.starts_at, row.session?.ends_at, row.session?.room_id, groupCode].join('|')
        : String(row.session?.id);
      if (!grouped.has(key)) grouped.set(key, { ...row, units: [] });
      grouped.get(key).units.push(row.unit);
    });
    return [...grouped.values()];
  }, [sessions]);

  const grid = React.useMemo(() => {
    const usedDays = new Set(sessions.map(row => Number(row.session?.day_of_week)));
    const days = [1, 2, 3, 4, 5, ...[6, 7].filter(day => usedDays.has(day))];
    const starts = sessions.map(row => minutesFromTime(row.session?.starts_at));
    const ends = sessions.map(row => minutesFromTime(row.session?.ends_at));
    const configuredStart = minutesFromTime(scheduleWindow.startsAt);
    const configuredEnd = minutesFromTime(scheduleWindow.endsAt);
    const start = Math.min(configuredStart, starts.length ? Math.floor(Math.min(...starts) / HOUR_MINUTES) * HOUR_MINUTES : configuredStart);
    const observedEnd = ends.length ? Math.ceil(Math.max(...ends) / HOUR_MINUTES) * HOUR_MINUTES : configuredEnd;
    const end = Math.max(start + HOUR_MINUTES, configuredEnd, observedEnd);
    const hours = Array.from({ length: Math.ceil((end - start) / HOUR_MINUTES) }, (_, index) => start + (index * HOUR_MINUTES));
    return { days, start, end, hours };
  }, [scheduleWindow, sessions]);

  const totals = React.useMemo(() => ({
    sessions: displaySessions.length,
    hours: displaySessions.reduce((sum, row) => sum + Math.max(0, minutesFromTime(row.session?.ends_at) - minutesFromTime(row.session?.starts_at)), 0) / 60,
    departments: new Set(displaySessions.map(row => row.department?.id).filter(Boolean)).size,
    units: new Set(sessions.map(row => row.unit?.id).filter(Boolean)).size,
    rooms: new Set(displaySessions.map(row => row.room?.id).filter(Boolean)).size,
  }), [displaySessions, sessions]);

  const selectedYear = years.find(row => String(row.id) === String(yearId));
  const selectedSemester = semesters.find(row => String(row.id) === String(semesterId));
  const trainerName = profile?.full_name || user?.user_metadata?.full_name || 'Trainer';
  const provisional = isStudent && sessions.some(row => row.batch?.status !== 'published');

  const download = () => timetableService.downloadPdf({
    sessions,
    title: isStudent ? 'Class Timetable' : `${trainerName} · Teaching Timetable`,
    period: `${selectedYear?.name || ''} · ${selectedSemester?.name || ''}`,
  });

  return <div className="space-y-4">
    <div className={`flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 ${isStudent ? '' : 'rounded-xl border bg-white p-3 shadow-sm'}`}>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          {!isStudent && onBack && <><button onClick={onBack} className="text-xs font-semibold text-blue-700 hover:underline">← Staff dashboard</button><span className="text-xs text-slate-300">/</span></>}
          <h2 className={isStudent ? 'text-2xl font-bold text-slate-950' : 'text-sm font-bold text-slate-950'}>{isStudent ? 'My class timetable' : 'My teaching timetable'}</h2>
          {!isStudent && <span className="text-xs text-slate-500">{totals.sessions} sessions · {Number.isInteger(totals.hours) ? totals.hours : totals.hours.toFixed(1)} hours · {totals.units} units</span>}
        </div>
        {isStudent && <p className="text-sm text-slate-500 mt-1">Sessions for your registered units only.</p>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {/* Compact stats for Student */}
        {isStudent && (
          <div className="hidden lg:flex items-center bg-white rounded-lg border border-slate-200 shadow-sm p-1 h-[40px]">
            <div className="flex items-center gap-2 px-3 border-r border-slate-100" title="Sessions">
              <CalendarDays className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-semibold text-slate-700">{totals.sessions}</span>
            </div>
            <div className="flex items-center gap-2 px-3 border-r border-slate-100" title="Contact hours">
              <Clock3 className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-semibold text-slate-700">{Number.isInteger(totals.hours) ? totals.hours : totals.hours.toFixed(1)}</span>
            </div>
            <div className="flex items-center gap-2 px-3 border-r border-slate-100" title="Registered units">
              <BookOpen className="h-4 w-4 text-indigo-500" />
              <span className="text-sm font-semibold text-slate-700">{totals.units}</span>
            </div>
            <div className="flex items-center gap-2 px-3" title="Rooms">
              <MapPin className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-semibold text-slate-700">{totals.rooms}</span>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 shadow-sm p-1">
          <select value={yearId} onChange={event => setYearId(event.target.value)} className="h-8 min-w-32 rounded bg-transparent px-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
            {years.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
          <div className="h-5 w-px bg-slate-200"></div>
          <select value={semesterId} onChange={event => setSemesterId(event.target.value)} className="h-8 min-w-32 rounded bg-transparent px-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
            {semesters.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-[40px]" onClick={load}><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
          <Button variant="outline" className="h-[40px]" onClick={download} disabled={!sessions.length}><Download className="mr-2 h-4 w-4" />PDF</Button>
        </div>
      </div>
    </div>

    {provisional && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"><b>Provisional timetable:</b> your registered-unit schedule is available, but the department has not published its final version. Changes may still occur.</div>}

    <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
      {loading ? <div className="p-12 text-center text-sm text-slate-500">Loading your timetable…</div> : sessions.length === 0 ? <div className="p-12 text-center text-slate-500">
        <BookOpen className="mx-auto mb-3 h-8 w-8 text-slate-400" />
        <p className="font-semibold text-slate-800">{isStudent ? 'No published class timetable for this period.' : 'No teaching sessions assigned for this period.'}</p>
        <p className="mt-1 text-sm">{isStudent ? 'Sessions appear after you register units and the department timetable is published.' : 'A session appears after the department timetable includes your approved trainer assignment.'}</p>
      </div> : <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Header row (Time) */}
          <div className="flex sticky top-0 z-30 bg-primary text-primary-foreground border-b">
            <div className="w-[90px] shrink-0 sticky left-0 z-40 bg-primary border-r flex items-center justify-center text-xs font-semibold uppercase h-12">Day</div>
            <div className="flex-1 flex">
              {grid.hours.map(minute => <div key={minute} className="flex-1 border-r border-primary-foreground/20 h-12 flex items-center justify-center text-sm font-semibold" title={`${formatTime(minute)}–${formatTime(minute + HOUR_MINUTES)}`}>
                {Math.floor(minute / HOUR_MINUTES)}:00
              </div>)}
            </div>
          </div>
          
          {/* Body rows (Days) */}
          {grid.days.map(day => <div key={day} className="flex group border-b last:border-b-0">
            <div className="w-[90px] shrink-0 sticky left-0 z-20 bg-white border-r flex items-center justify-center font-semibold text-sm h-[110px]">
              {DAYS[day]}
            </div>
            <div className="flex-1 relative bg-white flex">
              {/* Grid lines */}
              {grid.hours.map(minute => <div key={minute} className="flex-1 border-r group-odd:bg-slate-50/60" />)}
              
              {/* Sessions */}
              {displaySessions.filter(row => Number(row.session?.day_of_week) === day).map((row, index) => {
                const start = minutesFromTime(row.session?.starts_at);
                const end = minutesFromTime(row.session?.ends_at);
                if (end <= start) return null;
                const totalMinutes = grid.hours.length * HOUR_MINUTES;
                const leftPct = ((start - grid.start) / totalMinutes) * 100;
                const widthPct = ((end - start) / totalMinutes) * 100;
                const unitCodes = (row.units || [row.unit]).map(unit => unit?.code).filter(Boolean).join(' / ');
                const unitNames = (row.units || [row.unit]).map(unit => unit?.name).filter(Boolean).join(' / ');
                return <div key={row.session?.id || index} className="absolute top-1.5 bottom-1.5 z-10 overflow-hidden rounded-md border border-blue-400 bg-blue-50 px-2 py-1.5 text-xs text-slate-800 shadow-sm hover:border-blue-600 hover:shadow" style={{ left: `calc(${leftPct}% + 4px)`, width: `calc(${widthPct}% - 8px)` }} title={`${unitCodes} · ${unitNames}`}>
                  <div className="flex items-start justify-between gap-2"><b className="text-blue-950 truncate">{unitCodes}</b><span className="whitespace-nowrap font-medium text-blue-700">{formatTime(start)}–{formatTime(end)}</span></div>
                  <p className="mt-0.5 truncate font-medium">{unitNames}</p>
                  <p className="mt-1 truncate text-slate-600">{row.room?.name || 'Room pending'}</p>
                  <p className="truncate text-[11px] text-slate-500">{row.unit?.program?.name} · Level {row.unit?.program_level || row.unit?.program?.level || '—'} · Module {row.unit?.module_in_level || row.unit?.program?.module || '—'}</p>
                </div>;
              })}
            </div>
          </div>)}
        </div>
      </div>}
    </div>
    <ActionToast notice={notice} onClose={() => setNotice(null)} />
  </div>;
};

export default MyTimetable;
